import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RewardsMarketplace = ({ currentPoints }) => {
  const [rewards, setRewards] = useState([]);
  const [selectedTier, setSelectedTier] = useState('all');
  const [redeemingId, setRedeemingId] = useState(null);

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    // Mock data - replace with actual Supabase queries
    const mockRewards = [
      {
        id: 1,
        name: 'Free Guest Pass',
        description: 'Bring a friend for one free workout session',
        pointsCost: 500,
        tier: 'bronze',
        icon: 'UserPlus',
        category: 'guest',
        available: true,
        stock: 'Unlimited'
      },
      {
        id: 2,
        name: 'Gym Merchandise 20% Off',
        description: 'Get 20% discount on any gym merchandise',
        pointsCost: 750,
        tier: 'bronze',
        icon: 'ShoppingBag',
        category: 'discount',
        available: true,
        stock: 'Unlimited'
      },
      {
        id: 3,
        name: 'Personal Training Session',
        description: 'One complimentary 60-minute personal training session',
        pointsCost: 1500,
        tier: 'silver',
        icon: 'Dumbbell',
        category: 'training',
        available: true,
        stock: '12 remaining'
      },
      {
        id: 4,
        name: 'Premium Class Access',
        description: 'Access to 3 premium specialty classes',
        pointsCost: 2000,
        tier: 'silver',
        icon: 'Star',
        category: 'class',
        available: true,
        stock: 'Unlimited'
      },
      {
        id: 5,
        name: 'Massage Therapy Session',
        description: '60-minute sports massage therapy session',
        pointsCost: 3000,
        tier: 'gold',
        icon: 'Heart',
        category: 'wellness',
        available: true,
        stock: '5 remaining'
      },
      {
        id: 6,
        name: 'VIP Locker Upgrade',
        description: 'Upgrade to VIP locker for 3 months',
        pointsCost: 4000,
        tier: 'gold',
        icon: 'Lock',
        category: 'facility',
        available: true,
        stock: '3 remaining'
      }
    ];
    setRewards(mockRewards);
  };

  const handleRedeem = async (rewardId) => {
    setRedeemingId(rewardId);
    // Simulate API call
    setTimeout(() => {
      alert('Reward redeemed successfully! Check your email for details.');
      setRedeemingId(null);
    }, 1500);
  };

  const tiers = [
    { value: 'all', label: 'All Rewards' },
    { value: 'bronze', label: 'Bronze' },
    { value: 'silver', label: 'Silver' },
    { value: 'gold', label: 'Gold' }
  ];

  const getTierColor = (tier) => {
    const colors = {
      bronze: 'text-warning',
      silver: 'text-muted-foreground',
      gold: 'text-accent'
    };
    return colors?.[tier] || 'text-foreground';
  };

  const filteredRewards = selectedTier === 'all'
    ? rewards
    : rewards?.filter(r => r?.tier === selectedTier);

  return (
    <div className="card">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground mb-1">
            Rewards Marketplace
          </h2>
          <p className="text-sm text-muted-foreground">
            You have <span className="font-semibold text-primary">{currentPoints?.toLocaleString()}</span> points available
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {tiers?.map((tier) => (
            <button
              key={tier?.value}
              onClick={() => setSelectedTier(tier?.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedTier === tier?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {tier?.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredRewards?.map((reward) => {
          const canAfford = currentPoints >= reward?.pointsCost;
          return (
            <div
              key={reward?.id}
              className={`p-5 rounded-lg border-2 transition-all duration-250 ${
                canAfford
                  ? 'border-primary/30 bg-primary/5 hover:border-primary/50 hover:shadow-lg'
                  : 'border-border bg-muted/30 opacity-75'
              }`}
            >
              <div className="flex items-start gap-3 mb-4">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    canAfford ? 'bg-primary/20' : 'bg-muted'
                  }`}
                >
                  <Icon
                    name={reward?.icon}
                    size={24}
                    color={canAfford ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1">
                    {reward?.name}
                  </h3>
                  <span className={`text-xs font-medium capitalize ${getTierColor(reward?.tier)}`}>
                    {reward?.tier} Tier
                  </span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {reward?.description}
              </p>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-1">
                  <Icon name="Coins" size={16} color="var(--color-primary)" />
                  <span className="font-semibold text-primary">
                    {reward?.pointsCost?.toLocaleString()}
                  </span>
                  <span className="text-xs text-muted-foreground">points</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {reward?.stock}
                </span>
              </div>
              <Button
                variant={canAfford ? 'default' : 'outline'}
                fullWidth
                disabled={!canAfford || redeemingId === reward?.id}
                loading={redeemingId === reward?.id}
                onClick={() => handleRedeem(reward?.id)}
              >
                {canAfford ? 'Redeem Now' : 'Insufficient Points'}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RewardsMarketplace;